/**
 * Montagematerial aus der Belegung (Kapitel J).
 *
 * Klemmen, Schienen, Verbinder und Dachhaken haengen nicht an der Zahl der
 * Module, sondern daran, wie sie liegen. Vorher rechnete der Materialsatz
 * pauschal — zwei Mittelklemmen je Modul, vier Endklemmen je Flaeche — und
 * bei zwei Reihen zu zehn Modulen kamen 40 Mittel- und 4 Endklemmen heraus
 * statt 36 und 8 (Rueckmeldung aus dem ersten Test bei Wania, 10/2026).
 *
 * Die Regel dahinter: Ein Modul wird an seinen Langseiten geklemmt. Im
 * Hochformat stehen die Langseiten senkrecht, die Schienen laufen also
 * waagrecht entlang der Reihe, und Nachbarn in der Reihe teilen sich die
 * Klemme. Im Querformat liegen die Langseiten waagrecht, die Schienen laufen
 * vom First zur Traufe, und Nachbarn in der Spalte teilen sich die Klemme.
 *
 * Ein Strang ist eine Folge von Modulen, die ohne Luecke an derselben Schiene
 * haengen; ein Trennabstand im Feld beendet ihn. Je Strang:
 *
 *   zwei Schienen
 *   vier Endklemmen — zwei je Schiene, eine an jedem Ende
 *   Mittelklemmen — zwei je Fuge zwischen zwei Modulen
 *   Schienenstuecke je Schiene nach Stangenlaenge, Verbinder dazwischen
 *   Dachhaken je Schiene nach groesstem Abstand, an jedem Ende einer
 *
 * Gerechnet wird je Schiene, ohne Verschnitt ueber Straenge hinweg
 * auszugleichen — lieber eine Stange zu viel auf dem Dach als eine zu wenig.
 * Die Statik (Schnee, Wind) rechnet dieses Modul nicht; der Hakenabstand ist
 * ein eingestellter Wert und steht im Materialsatz. Sind Sparren oder Pfetten
 * eingetragen, sitzen die Haken auf ihnen (`tragwerk.ts`): waagrechte Schienen
 * (Hochformat) auf den Sparren, senkrechte (Querformat) auf den Pfetten.
 */
import { hakenAufTraegern } from './tragwerk.js';
/**
 * Die Straenge eines Feldes. Hochformat: Straenge laufen entlang der Reihen.
 * Querformat: entlang der Spalten.
 */
export function modulstraenge(plaetze, ausrichtung, modul, fugen) {
    // Entlang des Strangs liegt immer die Kurzseite: Nachbarn teilen sich die
    // Langseite. Die Fuge ist die, die quer zum Strang zwischen ihnen liegt.
    const kurzseiteM = Math.min(modul.breiteMm, modul.hoeheMm) / 1000;
    const fugeM = (ausrichtung === 'hoch' ? fugen.fugeQuerMm : fugen.fugeLaengsMm) / 1000;
    // Laengs der Schiene: x bei waagrechten (Hochformat), y bei senkrechten.
    const laengs = (p) => (ausrichtung === 'hoch' ? p.x : p.y);
    const linien = new Map();
    for (const p of plaetze) {
        const linie = ausrichtung === 'hoch' ? p.reihe : p.spalte;
        const lage = ausrichtung === 'hoch' ? p.spalte : p.reihe;
        const liste = linien.get(linie) ?? new Map();
        const werte = p.ecken?.map(laengs);
        liste.set(lage, werte && werte.length > 0 ? { von: Math.min(...werte), bis: Math.max(...werte) } : null);
        linien.set(linie, liste);
    }
    const straenge = [];
    const kreuzt = ausrichtung === 'hoch' ? 'sparren' : 'pfetten';
    const schliesse = (ausdehnungen) => {
        const anzahl = ausdehnungen.length;
        if (anzahl === 0)
            return;
        const strang = { anzahl, laengeM: anzahl * kurzseiteM + (anzahl - 1) * fugeM };
        if (ausdehnungen.every((a) => a !== null)) {
            const bekannt = ausdehnungen;
            strang.lage = {
                von: Math.min(...bekannt.map((a) => a.von)),
                bis: Math.max(...bekannt.map((a) => a.bis)),
                kreuzt,
            };
        }
        straenge.push(strang);
    };
    for (const linie of [...linien.keys()].sort((a, b) => a - b)) {
        const plaetzeDerLinie = linien.get(linie);
        const lagen = [...plaetzeDerLinie.keys()].sort((a, b) => a - b);
        let strang = [];
        let vorige = null;
        for (const lage of lagen) {
            const hier = plaetzeDerLinie.get(lage) ?? null;
            const davor = strang[strang.length - 1] ?? null;
            // Ein Trennabstand teilt den Strang, auch wenn die Plaetze im Raster
            // aufeinander folgen: Dafuer ist er da — die Schiene endet dort.
            const getrennt = hier !== null && davor !== null && hier.von - davor.bis > fugeM + 0.01;
            if (vorige !== null && (lage !== vorige + 1 || getrennt)) {
                schliesse(strang);
                strang = [];
            }
            strang.push(hier);
            vorige = lage;
        }
        schliesse(strang);
    }
    return straenge;
}
/** Mengen je Artikel fuer alle Straenge zusammen. */
export function montagemengen(straenge, system, traeger) {
    if (straenge.length === 0)
        return [];
    const SCHIENEN_JE_STRANG = 2;
    const ueberstand = system.ueberstandM ?? 0.05;
    let mittel = 0;
    let end = 0;
    let stuecke = 0;
    let verbinder = 0;
    let haken = 0;
    for (const strang of straenge) {
        end += 2 * SCHIENEN_JE_STRANG;
        mittel += (strang.anzahl - 1) * SCHIENEN_JE_STRANG;
        const schieneM = strang.laengeM + 2 * ueberstand;
        if (system.schiene && system.schiene.laengeM > 0) {
            const jeSchiene = Math.ceil(schieneM / system.schiene.laengeM - 1e-9);
            stuecke += jeSchiene * SCHIENEN_JE_STRANG;
            verbinder += (jeSchiene - 1) * SCHIENEN_JE_STRANG;
        }
        if (system.dachhaken) {
            // Auf den Traegern, wenn sie eingetragen sind und unter der Schiene
            // durchlaufen — sonst nach dem eingestellten Abstand.
            const lagen = strang.lage
                ? strang.lage.kreuzt === 'sparren'
                    ? traeger?.sparrenX
                    : traeger?.pfettenY
                : undefined;
            const aufTraegern = strang.lage && lagen && lagen.length > 0
                ? hakenAufTraegern(strang.lage.von - ueberstand, strang.lage.bis + ueberstand, lagen, system.dachhaken.abstandM)
                : null;
            if (aufTraegern !== null)
                haken += aufTraegern * SCHIENEN_JE_STRANG;
            else if (system.dachhaken.abstandM > 0)
                haken += (Math.ceil(schieneM / system.dachhaken.abstandM - 1e-9) + 1) * SCHIENEN_JE_STRANG;
        }
    }
    const zeilen = [];
    const dazu = (artikelId, menge, rolle) => {
        if (artikelId && menge > 0)
            zeilen.push({ artikelId, menge, rolle });
    };
    dazu(system.mittelklemme, mittel, 'mittelklemme');
    dazu(system.endklemme, end, 'endklemme');
    dazu(system.schiene?.artikelId, stuecke, 'schiene');
    dazu(system.schienenverbinder, verbinder, 'schienenverbinder');
    dazu(system.dachhaken?.artikelId, haken, 'dachhaken');
    return zeilen;
}
//# sourceMappingURL=montage.js.map